import * as React from "react";
import { useLocation, useNavigate, Link as RouterLink } from "react-router";

import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GradingIcon from "@mui/icons-material/Grading";
import HelpIcon from "@mui/icons-material/Help";
import HistoryIcon from "@mui/icons-material/History";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import PasswordIcon from "@mui/icons-material/Password";
import QrCodeIcon from "@mui/icons-material/QrCode2";
import RateReviewIcon from "@mui/icons-material/RateReview";
import SchoolIcon from "@mui/icons-material/School";
import SearchIcon from "@mui/icons-material/Search";
import SportsTennisIcon from "@mui/icons-material/SportsTennis";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Toolbar from "@mui/material/Toolbar";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import { PartnerLocationWithRelations } from "@volley/data";

import config from "../../config";
import fetchApi, { logFetchError } from "../../util/fetchApi";
import { CLUB_IN_CLUB_FEATURE } from "../Home/HomeScreenItems";
import {
    DUPR_APP_ASSESSOR_FEATURE,
    WPR_APP_ASSESSOR_FEATURE,
    isAssessmentAssessor,
} from "../Journeys/JourneyAssessment/constants";
import { PADEL_ASSESSMENT_START_URL } from "../Journeys/PadelAssessment/padelAppDone";
import { useCurrentUser } from "../hooks/currentUser";

import ClubInClubMenuItem from "./ClubInClubMenuItem";
import { useSelectedSport, type Sport } from "./context/sport";

const DISPLAY_CLUB_SUMMARY_CACHE_KEY = "volley.displayClubSummary";

interface UserHamburgerMenuProps {
    open: boolean;
    onClose: () => void;
}

interface UserMenuItemsProps {
    closeMenu: () => void;
}

function UserMenuItems({ closeMenu }: UserMenuItemsProps): React.JSX.Element {
    const { isAdmin, isAssessmentTester, features } = useCurrentUser();
    const location = useLocation();
    const navigate = useNavigate();
    const { selected, friendlyName, available, updateSelected } =
        useSelectedSport();
    const [open, setOpen] = React.useState(false);

    const [displayClubSummary, setDisplayClubSummary] = React.useState(false);

    React.useEffect(() => {
        if (isAdmin()) {
            setDisplayClubSummary(true);
        } else {
            const cachedDisplayClubSummary = window.sessionStorage.getItem(
                DISPLAY_CLUB_SUMMARY_CACHE_KEY,
            );
            if (cachedDisplayClubSummary === null) {
                fetchApi<PartnerLocationWithRelations[]>(
                    "/api/partner-locations",
                )
                    .then((assignedLocations) => {
                        const hasAssignedLocations =
                            assignedLocations.length > 0;
                        setDisplayClubSummary(hasAssignedLocations);
                        window.sessionStorage.setItem(
                            DISPLAY_CLUB_SUMMARY_CACHE_KEY,
                            String(hasAssignedLocations),
                        );
                    })
                    .catch((error) =>
                        logFetchError(
                            error,
                            "GET /api/partner-locations failed in UserHamburgerMenu component",
                        ),
                    );
            } else if (cachedDisplayClubSummary === "true") {
                setDisplayClubSummary(true);
            }
        }
    }, [isAdmin]);

    return (
        <>
            <ListItemButton
                onClick={(e) => {
                    e.stopPropagation();
                    setOpen(!open);
                }}
            >
                <ListItemIcon>
                    <SportsTennisIcon sx={{ color: "white" }} />
                </ListItemIcon>
                <ListItemText primary={friendlyName} sx={{ color: "white" }} />
                {open ? (
                    <ExpandLessIcon sx={{ color: "white" }} />
                ) : (
                    <ExpandMoreIcon sx={{ color: "white" }} />
                )}
            </ListItemButton>
            <Collapse in={open} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                    {Object.keys(available).map((sport) => (
                        <ListItemButton
                            key={sport}
                            sx={{ pl: 4 }}
                            onClick={() => {
                                if (sport === selected) {
                                    setOpen(false);
                                    closeMenu();
                                } else {
                                    updateSelected(sport as Sport);
                                    setOpen(false);
                                    closeMenu();
                                    if (location.pathname !== "/") {
                                        navigate("/");
                                    }
                                }
                            }}
                        >
                            <ListItemText
                                primary={available[sport as Sport]}
                                sx={{
                                    color:
                                        sport === selected
                                            ? "secondary.main"
                                            : "white",
                                }}
                            />
                        </ListItemButton>
                    ))}
                </List>
            </Collapse>
            <ListItemButton component={RouterLink} to="/tutorials">
                <ListItemIcon>
                    <HelpIcon sx={{ color: "white" }} />
                </ListItemIcon>
                <ListItemText primary="Tutorial" sx={{ color: "white" }} />
            </ListItemButton>
            {config.clubInClubUrl &&
                features.includes(CLUB_IN_CLUB_FEATURE) && (
                    <ClubInClubMenuItem
                        url={config.clubInClubUrl}
                        onClose={closeMenu}
                    />
                )}
            <ListItemButton component={RouterLink} to="/content/history/mine">
                <ListItemIcon>
                    <HistoryIcon sx={{ color: "white" }} />
                </ListItemIcon>
                <ListItemText
                    primary="Sharing & History"
                    sx={{ color: "white" }}
                />
            </ListItemButton>
            {features.includes("POINT_CLICK_AI") && (
                <ListItemButton
                    component={RouterLink}
                    to="/content/apps/workouts/experimental/point-click?mode=ai"
                >
                    <ListItemIcon>
                        <AutoFixHighIcon sx={{ color: "white" }} />
                    </ListItemIcon>
                    <ListItemText
                        primary="Racquet AI"
                        sx={{ color: "white" }}
                    />
                </ListItemButton>
            )}
            {features.includes("JOURNEY_INSTANCES_AT_A_GLANCE") &&
                !features.includes("ASSESSMENT_DASHBOARD_V2") && (
                    <ListItemButton
                        component={RouterLink}
                        to="/journeys/assessments"
                    >
                        <ListItemIcon>
                            <GradingIcon sx={{ color: "white" }} />
                        </ListItemIcon>
                        <ListItemText
                            primary="My Assessments"
                            sx={{ color: "white" }}
                        />
                    </ListItemButton>
                )}
            {features.includes("JOURNEY_CLASSES") && (
                <ListItemButton component={RouterLink} to="/journeys/classes">
                    <ListItemIcon>
                        <SchoolIcon sx={{ color: "white" }} />
                    </ListItemIcon>
                    <ListItemText primary="Classes" sx={{ color: "white" }} />
                </ListItemButton>
            )}
            {(features.includes("PRO_GRADING") ||
                features.includes("CONTACT_ANNOTATION")) && (
                <ListItemButton component={RouterLink} to="/pro-grading">
                    <ListItemIcon>
                        <RateReviewIcon sx={{ color: "white" }} />
                    </ListItemIcon>
                    <ListItemText
                        primary="Pro Grading"
                        sx={{ color: "white" }}
                    />
                </ListItemButton>
            )}
            {displayClubSummary && (
                <ListItemButton
                    component={RouterLink}
                    to="/content/club-summary"
                >
                    <ListItemIcon>
                        <LeaderboardIcon sx={{ color: "white" }} />
                    </ListItemIcon>
                    <ListItemText
                        primary="Club Summary"
                        sx={{ color: "white" }}
                    />
                </ListItemButton>
            )}
            {(isAdmin() || isAssessmentTester()) && (
                <ListItemButton
                    component={RouterLink}
                    to="/journeys/assessments"
                >
                    <ListItemIcon>
                        <GradingIcon sx={{ color: "white" }} />
                    </ListItemIcon>
                    <ListItemText
                        primary="Administer Assessment"
                        sx={{ color: "white" }}
                    />
                </ListItemButton>
            )}
            {features.includes(DUPR_APP_ASSESSOR_FEATURE) && (
                <ListItemButton
                    component={RouterLink}
                    to="/journeys/long-form?administer=1"
                >
                    <ListItemIcon>
                        <AssignmentIndIcon sx={{ color: "white" }} />
                    </ListItemIcon>
                    <ListItemText
                        primary="DUPR Assessment"
                        sx={{ color: "white" }}
                    />
                </ListItemButton>
            )}
            {features.includes(WPR_APP_ASSESSOR_FEATURE) && (
                <ListItemButton
                    component={RouterLink}
                    to={`${PADEL_ASSESSMENT_START_URL}?administer=1`}
                >
                    <ListItemIcon>
                        <AssignmentIndIcon sx={{ color: "white" }} />
                    </ListItemIcon>
                    <ListItemText
                        primary="WPR Assessment"
                        sx={{ color: "white" }}
                    />
                </ListItemButton>
            )}
            {isAssessmentAssessor(isAdmin(), features) && (
                <ListItemButton
                    component={RouterLink}
                    to="/journeys/assessments/unclaimed"
                >
                    <ListItemIcon>
                        <QrCodeIcon sx={{ color: "white" }} />
                    </ListItemIcon>
                    <ListItemText
                        primary="Unclaimed Assessments"
                        sx={{ color: "white" }}
                    />
                </ListItemButton>
            )}
            <ListItemButton component={RouterLink} to="/ball-cabinet-unlock">
                <ListItemIcon>
                    <PasswordIcon sx={{ color: "white" }} />
                </ListItemIcon>
                <ListItemText
                    primary="Ball Cabinet Unlock"
                    sx={{ color: "white" }}
                />
            </ListItemButton>
            {isAdmin() && (
                <>
                    <Divider
                        sx={{
                            borderColor: "white",
                            width: "80%",
                            margin: "auto",
                        }}
                    />
                    {features.includes("CONTENT_SEARCH") && (
                        <ListItemButton
                            component={RouterLink}
                            to="/content/search"
                        >
                            <ListItemIcon>
                                <SearchIcon sx={{ color: "white" }} />
                            </ListItemIcon>
                            <ListItemText
                                primary="Content Search"
                                sx={{ color: "white" }}
                            />
                        </ListItemButton>
                    )}
                    <ListItemButton component={RouterLink} to="/admin">
                        <ListItemIcon>
                            <AdminPanelSettingsIcon sx={{ color: "white" }} />
                        </ListItemIcon>
                        <ListItemText
                            primary="Admin Panel"
                            sx={{ color: "white" }}
                        />
                    </ListItemButton>
                    <ListItemButton component={RouterLink} to="/exp">
                        <ListItemText
                            inset
                            primary="Experiences"
                            sx={{ color: "white" }}
                        />
                    </ListItemButton>
                    <ListItemButton
                        component={RouterLink}
                        to="/content/apps/workouts/experimental/point-click"
                    >
                        <ListItemText
                            inset
                            primary="Tap & Throw"
                            sx={{ color: "white" }}
                        />
                    </ListItemButton>
                    <ListItemButton>
                        <ListItemText
                            inset
                            primary="Software Build:"
                            secondary={config.version}
                            sx={{
                                color: "white",
                                "& .MuiListItemText-secondary": {
                                    color: "white",
                                },
                            }}
                        />
                    </ListItemButton>
                </>
            )}
        </>
    );
}

const DRAWER_WIDTH = 240;

export default function UserHamburgerMenu({
    open,
    onClose,
}: UserHamburgerMenuProps): React.JSX.Element {
    const theme = useTheme();
    const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));

    if (isSmUp) {
        return (
            <Drawer
                variant="permanent"
                open={open}
                onClose={onClose}
                slotProps={{
                    paper: {
                        sx: {
                            backgroundColor: "primary.dark",
                            borderBottomRightRadius: 24,
                            borderTopRightRadius: 24,
                            width: DRAWER_WIDTH,
                        },
                    },
                }}
            >
                <Toolbar />
                <List>
                    <UserMenuItems closeMenu={onClose} />
                </List>
            </Drawer>
        );
    }

    return (
        <Drawer
            variant="temporary"
            open={open}
            onClose={onClose}
            slotProps={{
                paper: {
                    sx: {
                        backgroundColor: "primary.dark",
                        borderBottomRightRadius: 24,
                        borderTopRightRadius: 24,
                        width: DRAWER_WIDTH,
                        borderRight: "2px solid",
                        borderRightColor: "primary.main",
                    },
                },
            }}
        >
            <Toolbar sx={{ justifyContent: "flex-end" }}>
                <IconButton onClick={onClose}>
                    <CloseIcon sx={{ color: "white" }} />
                </IconButton>
            </Toolbar>
            <List onClick={onClose}>
                <UserMenuItems closeMenu={onClose} />
            </List>
        </Drawer>
    );
}
